import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { recruitingAPI } from "../services/recruitingApi";
import { Chip, Menu, MenuItem } from "@mui/material";
import {
  APPLICATION_STATUS_CONFIG,
  APPLICATION_STATUS_ORDER,
  APPLICATION_REJECTION_REASONS,
  APPLICATION_VALID_TRANSITIONS,
} from "../components/StatusControl/configs/applicationConfig";
import { CANDIDATE_STATUS_CONFIG } from "../components/StatusControl/configs/candidateConfig";
import { JOB_STATUS_CONFIG } from "../components/StatusControl/configs/jobConfig";
import { StatusBadge } from "../components/StatusControl/StatusBadge";
import ApplicationStatusTimeline from "../components/ApplicationStatusTimeline";
import ApplicationStatusHistory from "../components/ApplicationStatusHistory";
import SuccessConfirmationDialog from "../components/StatusControl/SuccessConfirmationDialog";
import type {
  ApplicationStatus,
  ApplicationStatusHistoryEntry,
} from "../types/shared.types";
import "./ApplicationDetail.css";

const normalizeApplicationStatus = (status: string): string =>
  status === "active" || status === "new" ? "applied" : status;

const getMatchScoreColor = (score: number): string => {
  if (score >= 80) return "#10b981";
  if (score >= 50) return "#f59e0b";
  return "#ef4444";
};

type TabType = "overview" | "activity" | "history";

interface ApplicationData {
  id: number;
  candidate_id: number;
  job_id: number;
  status: string;
  expected_rate?: number;
  ai_match_score?: number;
  ai_match_override_justification?: string;
  rejection_reason?: string;
  rejection_reason_note?: string;
  rejected_from_status?: string;
  rejected_at?: string;
  status_changed_at?: string;
  status_changed_by?: number;
  status_changed_by_name?: string;
  owner_user_id?: number;
  owner_name?: string;
  candidate_first_name?: string;
  candidate_last_name?: string;
  candidate_email?: string;
  candidate_phone?: string;
  candidate_location?: string;
  candidate_source?: string;
  candidate_status?: string;
  main_skill?: string;
  seniority?: string;
  role_type?: string;
  job_title?: string;
  department?: string;
  job_location?: string;
  job_salary_range?: string;
  job_status?: string;
  created_at: string;
  updated_at: string;
}

interface NoteData {
  id: number;
  content: string;
  author_name?: string;
  created_at: string;
}

const ApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(["recruiting", "common"]);
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [newNote, setNewNote] = useState("");
  const [showTransitionPanel, setShowTransitionPanel] = useState(false);
  const [transitionNote, setTransitionNote] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionNote, setRejectionNote] = useState("");
  const [justification, setJustification] = useState("");
  const [successMetadata, setSuccessMetadata] = useState<any>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);

  const applicationId = Number(id);

  // Fetch application details
  const {
    data: appResponse,
    isLoading,
    error,
  } = useQuery(
    ["application", applicationId],
    () => recruitingAPI.getApplication(applicationId),
    { enabled: !!applicationId },
  );

  // Fetch status history
  const { data: historyResponse } = useQuery(
    ["application-status-history", applicationId],
    () => recruitingAPI.getApplicationStatusHistory(applicationId),
    { enabled: !!applicationId },
  );

  // Fetch notes
  const { data: notesResponse } = useQuery(
    ["application-notes", applicationId],
    () => recruitingAPI.getNotes("application", applicationId),
    { enabled: !!applicationId && activeTab === "activity" },
  );

  // Update status mutation
  const updateStatusMutation = useMutation(
    async (payload: {
      status: ApplicationStatus;
      note?: string;
      override_justification?: string;
    }) => {
      return recruitingAPI.updateApplicationStatus(
        applicationId,
        payload.status,
        {
          note: payload.note,
          override_justification: payload.override_justification,
        },
      );
    },
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries(["application", applicationId]);
        queryClient.invalidateQueries([
          "application-status-history",
          applicationId,
        ]);
        queryClient.invalidateQueries("applications");
        setShowTransitionPanel(false);
        setTransitionNote("");
        setJustification("");

        // Show success confirmation dialog when _metadata is present (US1-US4)
        if (data?._metadata) {
          setSuccessMetadata(data._metadata);
          setShowSuccessDialog(true);
        }
      },
      onError: (err: Error) => {
        alert(`Status update failed: ${err.message}`);
      },
    },
  );

  // Reject mutation
  const rejectMutation = useMutation(
    async () => {
      return recruitingAPI.rejectApplication(applicationId, rejectionReason, {
        rejection_reason_note: rejectionNote || undefined,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["application", applicationId]);
        queryClient.invalidateQueries([
          "application-status-history",
          applicationId,
        ]);
        queryClient.invalidateQueries("applications");
        setShowTransitionPanel(false);
        setRejectionReason("");
        setRejectionNote("");
      },
      onError: (err: Error) => {
        alert(`Rejection failed: ${err.message}`);
      },
    },
  );

  // Add note mutation
  const addNoteMutation = useMutation(
    async (content: string) => {
      return recruitingAPI.createNote({
        entity_type: "application",
        entity_id: applicationId,
        content,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["application-notes", applicationId]);
        setNewNote("");
      },
      onError: (err: Error) => {
        alert(`Failed to add note: ${err.message}`);
      },
    },
  );

  const application: ApplicationData | undefined = (appResponse as any)?.data;
  const statusHistory: ApplicationStatusHistoryEntry[] =
    (historyResponse as any)?.data || [];
  const notes: NoteData[] = (notesResponse as any)?.data || [];

  if (isLoading) {
    return (
      <div className="application-detail-loading">
        <div className="loading-spinner" />
        <p>Loading application...</p>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="application-detail-error">
        <h2>Application not found</h2>
        <p>
          {(error as Error)?.message || "Could not load application details"}
        </p>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  const currentStatus = normalizeApplicationStatus(application.status);
  const statusConfig = APPLICATION_STATUS_CONFIG[currentStatus];
  const allowedTransitions = APPLICATION_VALID_TRANSITIONS[currentStatus] || [];
  const candidateName =
    `${application.candidate_first_name || ""} ${application.candidate_last_name || ""}`.trim();
  const daysInStatus = application.status_changed_at
    ? Math.max(
        0,
        Math.floor(
          (Date.now() - new Date(application.status_changed_at).getTime()) /
            (1000 * 60 * 60 * 24),
        ),
      )
    : null;

  const handleStatusTransition = (targetStatus: string) => {
    // Not matched → Approved requires justification
    if (currentStatus === "not_matched_ai" && targetStatus === "approved") {
      if (!justification.trim()) {
        alert("Override justification is required");
        return;
      }
      updateStatusMutation.mutate({
        status: targetStatus as ApplicationStatus,
        note: transitionNote || undefined,
        override_justification: justification,
      });
      return;
    }

    if (targetStatus === "rejected") {
      if (!rejectionReason) {
        alert("Please select a rejection reason");
        return;
      }
      rejectMutation.mutate();
      return;
    }

    updateStatusMutation.mutate({
      status: targetStatus as ApplicationStatus,
      note: transitionNote || undefined,
    });
  };

  return (
    <div className="application-detail">
      {/* Header */}
      <div className="application-detail-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          &larr; Back
        </button>
        <div className="header-title">
          <h1>
            <button
              type="button"
              className="header-link"
              onClick={() =>
                navigate(`/candidates/${application.candidate_id}`)
              }
            >
              {candidateName || "Unknown Candidate"}
            </button>
            <span className="header-arrow"> &rarr; </span>
            <button
              type="button"
              className="header-link"
              onClick={() => navigate(`/jobs/${application.job_id}`)}
            >
              {application.job_title || "Unknown Job"}
            </button>
          </h1>
        </div>
      </div>

      {/* Status Control */}
      <div className="application-status-control">
        <div className="status-badge-section">
          <Chip
            label={statusConfig?.label || currentStatus}
            sx={{
              backgroundColor: statusConfig?.bgColor || "#f3f4f6",
              color: statusConfig?.color || "#374151",
              border: `2px solid ${statusConfig?.color || "#d1d5db"}`,
              fontWeight: 600,
              fontSize: "14px",
              height: "36px",
            }}
          />
          {daysInStatus !== null && (
            <span className="days-in-status">{daysInStatus}d in status</span>
          )}
        </div>

        <div className="status-timeline-section">
          <ApplicationStatusTimeline
            currentStatus={currentStatus}
            statusChangedAt={application.status_changed_at}
            rejectedFromStatus={application.rejected_from_status}
          />
        </div>

        {/* Transition Buttons — 3-zone layout */}
        {allowedTransitions.length > 0 &&
          (() => {
            const nonRejected = allowedTransitions.filter(
              (s) => s !== "rejected",
            );
            const forwardTransition = nonRejected[0] || null;
            const backwardTransitions = nonRejected.slice(1);
            const canReject = allowedTransitions.includes("rejected");

            return (
              <div className="status-actions">
                {!showTransitionPanel ? (
                  <div className="status-actions-bar">
                    {/* Zone A: Primary CTA (forward step) */}
                    {forwardTransition &&
                      (() => {
                        const fwdConfig =
                          APPLICATION_STATUS_CONFIG[forwardTransition];
                        return (
                          <button
                            onClick={() => {
                              if (
                                currentStatus === "not_matched_ai" &&
                                forwardTransition === "approved"
                              ) {
                                setShowTransitionPanel(true);
                              } else {
                                handleStatusTransition(forwardTransition);
                              }
                            }}
                            disabled={updateStatusMutation.isLoading}
                            className="forward-cta"
                            style={{
                              backgroundColor: fwdConfig?.color || "#1e40af",
                              borderColor: fwdConfig?.color || "#1e40af",
                            }}
                          >
                            {fwdConfig?.label || forwardTransition}
                            {updateStatusMutation.isLoading ? "..." : ""}
                          </button>
                        );
                      })()}

                    {/* Zone B: "More" dropdown for backward transitions */}
                    {backwardTransitions.length > 0 && (
                      <>
                        <button
                          onClick={(e) => setMoreAnchorEl(e.currentTarget)}
                          className="more-actions-btn"
                        >
                          More &#9662;
                        </button>
                        <Menu
                          anchorEl={moreAnchorEl}
                          open={Boolean(moreAnchorEl)}
                          onClose={() => setMoreAnchorEl(null)}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                        >
                          {backwardTransitions.map((targetStatus) => {
                            const bwdConfig =
                              APPLICATION_STATUS_CONFIG[targetStatus];
                            return (
                              <MenuItem
                                key={targetStatus}
                                onClick={() => {
                                  setMoreAnchorEl(null);
                                  if (
                                    currentStatus === "not_matched_ai" &&
                                    targetStatus === "approved"
                                  ) {
                                    setShowTransitionPanel(true);
                                  } else {
                                    handleStatusTransition(targetStatus);
                                  }
                                }}
                                disabled={updateStatusMutation.isLoading}
                              >
                                <span
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor:
                                      bwdConfig?.color || "#6b7280",
                                    display: "inline-block",
                                    marginRight: 8,
                                    flexShrink: 0,
                                  }}
                                />
                                {bwdConfig?.label || targetStatus}
                              </MenuItem>
                            );
                          })}
                        </Menu>
                      </>
                    )}

                    {/* Zone C: Reject button */}
                    {canReject && (
                      <button
                        onClick={() => setShowTransitionPanel(true)}
                        disabled={rejectMutation.isLoading}
                        className="transition-btn reject-btn"
                      >
                        &times; Reject
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="transition-panel">
                    {/* Rejection form */}
                    {allowedTransitions.includes("rejected") && (
                      <div className="rejection-form">
                        <select
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          className="rejection-reason-select"
                        >
                          <option value="">Select rejection reason...</option>
                          {Object.entries(APPLICATION_REJECTION_REASONS).map(
                            ([key, config]) => (
                              <option key={key} value={key}>
                                {config.label}
                              </option>
                            ),
                          )}
                        </select>
                        <textarea
                          value={rejectionNote}
                          onChange={(e) => setRejectionNote(e.target.value)}
                          placeholder="Additional notes (optional)"
                          rows={2}
                          className="transition-note-input"
                        />
                      </div>
                    )}

                    {/* Override justification (not_matched → approved) */}
                    {currentStatus === "not_matched_ai" && (
                      <div className="justification-form">
                        <label htmlFor="override-justification">
                          Override Justification (required):
                        </label>
                        <textarea
                          id="override-justification"
                          value={justification}
                          onChange={(e) => setJustification(e.target.value)}
                          placeholder="Why should this candidate be approved despite AI not matching?"
                          rows={2}
                          className="transition-note-input"
                        />
                      </div>
                    )}

                    <textarea
                      value={transitionNote}
                      onChange={(e) => setTransitionNote(e.target.value)}
                      placeholder="Transition note (optional)"
                      rows={1}
                      className="transition-note-input"
                    />

                    <div className="transition-panel-actions">
                      {rejectionReason && (
                        <button
                          onClick={() => handleStatusTransition("rejected")}
                          disabled={rejectMutation.isLoading}
                          className="transition-btn reject-btn"
                        >
                          Confirm Reject
                        </button>
                      )}
                      {currentStatus === "not_matched_ai" &&
                        justification.trim() && (
                          <button
                            onClick={() => handleStatusTransition("approved")}
                            disabled={updateStatusMutation.isLoading}
                            className="transition-btn approve-btn"
                          >
                            Override & Approve
                          </button>
                        )}
                      <button
                        onClick={() => {
                          setShowTransitionPanel(false);
                          setRejectionReason("");
                          setRejectionNote("");
                          setJustification("");
                          setTransitionNote("");
                        }}
                        className="transition-btn cancel-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

        {/* Rejection info for rejected applications */}
        {currentStatus === "rejected" && application.rejection_reason && (
          <div className="rejection-info">
            <strong>Rejection Reason:</strong>{" "}
            {APPLICATION_REJECTION_REASONS[application.rejection_reason]
              ?.label || application.rejection_reason}
            {application.rejected_from_status && (
              <span className="rejected-from">
                {" "}
                (from{" "}
                {APPLICATION_STATUS_CONFIG[application.rejected_from_status]
                  ?.label || application.rejected_from_status}
                )
              </span>
            )}
            {application.rejection_reason_note && (
              <p className="rejection-note">
                {application.rejection_reason_note}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="application-tabs">
        {(["overview", "activity", "history"] as TabType[]).map((tab) => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "activity"
              ? "Notes"
              : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="application-tab-content">
        {activeTab === "overview" && (
          <div className="overview-tab">
            <div className="overview-cards">
              {/* Candidate Card */}
              <div className="info-card">
                <h3>Candidate</h3>
                <div className="info-card-body">
                  <div className="info-row">
                    <span className="info-label">Name</span>
                    <button
                      type="button"
                      className="info-value clickable"
                      onClick={() =>
                        navigate(`/candidates/${application.candidate_id}`)
                      }
                    >
                      {candidateName || "Unknown"}
                    </button>
                  </div>
                  {application.candidate_email && (
                    <div className="info-row">
                      <span className="info-label">Email</span>
                      <span className="info-value">
                        {application.candidate_email}
                      </span>
                    </div>
                  )}
                  {application.candidate_phone && (
                    <div className="info-row">
                      <span className="info-label">Phone</span>
                      <span className="info-value">
                        {application.candidate_phone}
                      </span>
                    </div>
                  )}
                  {application.candidate_location && (
                    <div className="info-row">
                      <span className="info-label">Location</span>
                      <span className="info-value">
                        {application.candidate_location}
                      </span>
                    </div>
                  )}
                  {application.candidate_source && (
                    <div className="info-row">
                      <span className="info-label">Source</span>
                      <span className="info-value">
                        {application.candidate_source}
                      </span>
                    </div>
                  )}
                  {application.candidate_status && (
                    <div className="info-row">
                      <span className="info-label">Status</span>
                      <StatusBadge
                        status={application.candidate_status}
                        statusConfig={CANDIDATE_STATUS_CONFIG}
                        defaultStatus="preparation"
                        size="small"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Job Card */}
              <div className="info-card">
                <h3>Job</h3>
                <div className="info-card-body">
                  <div className="info-row">
                    <span className="info-label">Title</span>
                    <button
                      type="button"
                      className="info-value clickable"
                      onClick={() => navigate(`/jobs/${application.job_id}`)}
                    >
                      {application.job_title || "Unknown"}
                    </button>
                  </div>
                  {application.department && (
                    <div className="info-row">
                      <span className="info-label">Department</span>
                      <span className="info-value">
                        {application.department}
                      </span>
                    </div>
                  )}
                  {application.job_location && (
                    <div className="info-row">
                      <span className="info-label">Location</span>
                      <span className="info-value">
                        {application.job_location}
                      </span>
                    </div>
                  )}
                  {application.job_salary_range && (
                    <div className="info-row">
                      <span className="info-label">Salary Range</span>
                      <span className="info-value">
                        {application.job_salary_range}
                      </span>
                    </div>
                  )}
                  {application.job_status && (
                    <div className="info-row">
                      <span className="info-label">Status</span>
                      <StatusBadge
                        status={application.job_status}
                        statusConfig={JOB_STATUS_CONFIG}
                        defaultStatus="role_in_preparation"
                        size="small"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Application Details */}
            <div className="overview-details">
              <h3>Application Details</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Applied</span>
                  <span className="detail-value">
                    {new Date(application.created_at).toLocaleDateString()}
                  </span>
                </div>
                {application.expected_rate != null && (
                  <div className="detail-item">
                    <span className="detail-label">Expected Rate</span>
                    <span className="detail-value">
                      ${application.expected_rate}/day
                    </span>
                  </div>
                )}
                {application.ai_match_score != null && (
                  <div className="detail-item">
                    <span className="detail-label">AI Match Score</span>
                    <div className="ai-match-bar-container">
                      <div
                        className="ai-match-bar"
                        style={{
                          width: `${Math.min(100, application.ai_match_score)}%`,
                          backgroundColor: getMatchScoreColor(
                            application.ai_match_score,
                          ),
                        }}
                      />
                      <span className="ai-match-label">
                        {application.ai_match_score}%
                      </span>
                    </div>
                  </div>
                )}
                {daysInStatus !== null && (
                  <div className="detail-item">
                    <span className="detail-label">Days in Status</span>
                    <span className="detail-value">{daysInStatus}</span>
                  </div>
                )}
                {application.owner_name && (
                  <div className="detail-item">
                    <span className="detail-label">Owner</span>
                    <span className="detail-value">
                      {application.owner_name}
                    </span>
                  </div>
                )}
                {application.main_skill && (
                  <div className="detail-item">
                    <span className="detail-label">Skill</span>
                    <span className="detail-value">
                      {application.main_skill}
                    </span>
                  </div>
                )}
                {application.seniority && (
                  <div className="detail-item">
                    <span className="detail-label">Seniority</span>
                    <span className="detail-value">
                      {application.seniority}
                    </span>
                  </div>
                )}
                {application.role_type && (
                  <div className="detail-item">
                    <span className="detail-label">Role Type</span>
                    <span className="detail-value">
                      {application.role_type}
                    </span>
                  </div>
                )}
                {application.ai_match_override_justification && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Override Justification</span>
                    <div className="override-justification-box">
                      {application.ai_match_override_justification}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "activity" && (
          <div className="activity-tab">
            {/* Add Note */}
            <div className="add-note-section">
              <h3>Add Note</h3>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note about this application..."
                rows={3}
                className="note-input"
              />
              <button
                onClick={() => {
                  if (newNote.trim()) addNoteMutation.mutate(newNote.trim());
                }}
                disabled={!newNote.trim() || addNoteMutation.isLoading}
                className="add-note-btn"
              >
                {addNoteMutation.isLoading ? "Adding..." : "Add Note"}
              </button>
            </div>

            {/* Notes List */}
            <div className="notes-list">
              <h3>Notes & Activity</h3>
              {notes.length === 0 ? (
                <p className="empty-state">No notes yet</p>
              ) : (
                notes.map((note) => (
                  <div key={note.id} className="note-item">
                    <div className="note-header">
                      <span className="note-author">
                        {note.author_name || "System"}
                      </span>
                      <span className="note-date">
                        {new Date(note.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="note-content">{note.content}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === "history" && (
          <ApplicationStatusHistory history={statusHistory} />
        )}
      </div>

      {/* Success Confirmation Dialog (US1-US4 cross-entity feedback) */}
      {showSuccessDialog && successMetadata && (
        <SuccessConfirmationDialog
          open={showSuccessDialog}
          onClose={() => {
            setShowSuccessDialog(false);
            setSuccessMetadata(null);
          }}
          metadata={successMetadata}
          jobTitle={application?.job_title}
          candidateName={candidateName}
          onJobStatusUpdate={(jobId) => {
            navigate(`/jobs/${application?.job_id}`);
          }}
        />
      )}
    </div>
  );
};

export default ApplicationDetail;
